import { Injectable } from '@angular/core';
import { BackendService } from '@toro/common/shared/services/new-http/backend.service';
import { BehaviorSubject, defer, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  IPortabilityRequest,
  IUserRequests,
  IPortabilityStatusHistoryItem,
  IPortabilityStatusHistoryResponse,
} from '../../my-requests/custody-portability-requests.models';
import {
  IEffectuationType
} from '../../portability-flow/portability-effectuation-type-flow/portability-effectuation-type-flow.models';
import {
  IBrokerInfo,
  IInstitutionData,
} from '../../portability-flow/portability-institution-flow/portability-institution-flow.models';
import {
  EffectuationType,
  IProductsData
} from '../../portability-flow/portability-products-flow/portability-products-flow.models';
import { PortabilityType } from '../../portability-flow/portability-type-flow/portability-type-flow.models';
import {
  IFixedIncomeOutgoingRequest,
  IFundIncomingRequest,
  IFundOutgoingRequest,
  IOptionOutgoingRequest,
  IPortabilitySessionData,
  IProceedIncomingRequest,
  IProceedOutgoingRequest,
  ISendIncomingRequest,
  ISendOutgoingRequest,
  ISendPortabilityResponse,
  IStockOutgoingRequest,
  IStructuredOperationIncomingRequest,
  IStructuredOperationOutgoingRequest,
} from '../../portability-send-modal/portability-send-modal.models';

export enum SendStage {
  PREPARING = 'Preparando seus dados...',
  SENDING = 'Enviando seus dados para B3...',
}

function getParsedFromSession<T>(key: string): T {
  const raw = sessionStorage.getItem(key);
  if (!raw) throw new Error(`Missing sessionStorage item: ${key}`);
  try {
    return JSON.parse(raw) as T;
  } catch {
    throw new Error(`Failed to parse sessionStorage item: ${key}`);
  }
}

@Injectable({
  providedIn: 'root',
})
export class PortabilityService extends BackendService {
  public stageSubject = new BehaviorSubject<SendStage>(SendStage.PREPARING);
  public stage$ = this.stageSubject.asObservable();

  public createOutgoing(cblc: string): Observable<ISendPortabilityResponse> {

    return defer(() => {
      this.stageSubject.next(SendStage.PREPARING);
      const sessionData = this.getSessionData(cblc);

      const payload: ISendOutgoingRequest = {
        originInvestorCode: sessionData.cblc,
        destinationDocumentNumber: sessionData.portabilityInstitutionData.brokerCnpj,
        destinationInvestorCode: sessionData.portabilityInstitutionData.accountNumber,
        destinationCustodianCode: sessionData.portabilityInstitutionData.brokerCode.toString(),
        totalPortability: sessionData.portabilityEffectuationTypeData.type === EffectuationType.Total,
        consent: {
          portabilityTermsAndPolicies: {
            accepted: sessionData.portabilityProductsData.acceptedTerm,
            date: sessionData.portabilityProductsData.acceptedDate,
          },
          temporaryTradingLock: {
            accepted: sessionData.portabilityTypeData.accepted,
            date: sessionData.portabilityTypeData.acceptDate,
          }
        },
        stocks: sessionData.portabilityProductsData.products.variableIncome.stocks
          .filter((p) => p.selected)
          .map((p): IStockOutgoingRequest => {
            return {
              assetCode: p.code,
              quantity: p.transferQuantity,
            }
          }
          ),
        proceeds: sessionData.portabilityProductsData.products.variableIncome.proceeds
          .filter((p) => p.selected)
          .map((p): IProceedOutgoingRequest => {
            return {
              assetCode: p.code,
              quantity: p.transferQuantity,
            }
          }
          ),
        options: sessionData.portabilityProductsData.products.variableIncome.options
          .filter((p) => p.selected)
          .map((p): IOptionOutgoingRequest => {
            return {
              assetCode: p.code,
              quantity: p.transferQuantity,
            }
          }
          ),
        structuredOperations: sessionData.portabilityProductsData.products.variableIncome.structuredOperations
          .filter((p) => p.selected)
          .map((p): IStructuredOperationOutgoingRequest => {
            return {
              id: p.id,
            }
          }
          ),
        funds: sessionData.portabilityProductsData.products.funds
          .filter((p) => p.selected)
          .map((p): IFundOutgoingRequest => {
            return {
              assetCode: p.id
            }
          }
          ),
        fixedIncomes: sessionData.portabilityProductsData.products.fixedIncome
          .filter((p) => p.selected)
          .map((p): IFixedIncomeOutgoingRequest => {
            return {
              assetCode: p.code,
              quantity: p.transferQuantity,
            }
          }
          ),
      };

      this.stageSubject.next(SendStage.SENDING);

      return this.api.post('/stvm/orquestrador/api/v1/custody/portability/outgoing', payload);
    })
  };

  public createIncoming(cblc: string): Observable<ISendPortabilityResponse> {

    return defer(() => {
      this.stageSubject.next(SendStage.PREPARING);
      const sessionData = this.getSessionData(cblc);

      const filterIncoming = (products: any[], isProceeds: boolean = false) => {
        if (!products) return [];
        return products.filter(p => {
          const hasCode = p.saved === true && p.code && p.code.trim() !== '';

          const hasValidQuantity = isProceeds ? true : (p.transferQuantity > 0);

          return hasCode && hasValidQuantity;
        });
      };

      const payload: ISendIncomingRequest = {
        originDocumentNumber: sessionData.portabilityInstitutionData.brokerCnpj,
        originInvestorCode: sessionData.portabilityInstitutionData.accountNumber,
        originCustodianCode: sessionData.portabilityInstitutionData.brokerCode.toString(),
        totalPortability: sessionData.portabilityEffectuationTypeData.type === EffectuationType.Total,
        confirm: {
          tradingNotes: {
            submitted: sessionData.portabilityProductsData.tradingNotesSubmitted,
            date: sessionData.portabilityProductsData.tradingNotesSubmittedDate,
          }
        },
        fixedIncomes: filterIncoming(sessionData.portabilityProductsData.products.fixedIncome)
          .map((p): IFixedIncomeOutgoingRequest => ({
            assetCode: p.code,
            quantity: p.transferQuantity,
          })),
        proceeds: filterIncoming(sessionData.portabilityProductsData.products.variableIncome.proceeds, true)
          .map((p): IProceedIncomingRequest => ({
            assetCode: p.code,
          })),
        options: filterIncoming(sessionData.portabilityProductsData.products.variableIncome.options)
          .map((p): IOptionOutgoingRequest => ({
            assetCode: p.code,
            quantity: p.transferQuantity,
          })),
        structuredOperations: filterIncoming(sessionData.portabilityProductsData.products.variableIncome.structuredOperations)
          .map((p): IStructuredOperationIncomingRequest => ({
            assetCode: p.code,
            quantity: p.transferQuantity,
          })),
        funds: filterIncoming(sessionData.portabilityProductsData.products.funds)
          .map((p): IFundIncomingRequest => ({
            assetCode: p.code,
            quantity: p.transferQuantity,
          })),
        stocks: filterIncoming(sessionData.portabilityProductsData.products.variableIncome.stocks)
          .map((p): IStockOutgoingRequest => ({
            assetCode: p.code,
            quantity: p.transferQuantity,
          })),
      };

      this.stageSubject.next(SendStage.SENDING);

      return this.api.post('/stvm/orquestrador/api/v1/custody/portability/incoming', payload);
    })
  };

  public approveAuthorization(protocol: string): Observable<ISendPortabilityResponse> {
    return defer(() => {
      this.stageSubject.next(SendStage.PREPARING);
      this.stageSubject.next(SendStage.SENDING);

      return this.api.patch(`/stvm/orquestrador/api/v1/request/${protocol}/approve-portability-authorization`, {});
    })
  };

  public rejectAuthorization(protocol: string): Observable<ISendPortabilityResponse> {
    return defer(() => {
      this.stageSubject.next(SendStage.PREPARING);
      this.stageSubject.next(SendStage.SENDING);

      return this.api.patch(`/stvm/orquestrador/api/v1/request/${protocol}/reject-portability-authorization`, {});
    })
  };

  public reject(cblc: string, protocol: string): Observable<ISendPortabilityResponse> {
    return defer(() => {
      this.stageSubject.next(SendStage.PREPARING);
      this.stageSubject.next(SendStage.SENDING);

      return this.api.patch(`/stvm/orquestrador/api/v1/request/${protocol}/reject`, {});
    })
  };

  public getInstitutions(): Observable<IBrokerInfo[]> {
    return this.api.get<IBrokerInfo[]>('/stvm/orquestrador/api/v1/brokerages');
  }

  public getPortabilityRequests(cblc: string): Observable<IPortabilityRequest[]> {
    return this.api.get<IUserRequests>(`/stvm/orquestrador/api/v1/customer/${cblc}/requests`)
      .pipe(
        map(req => req.requests)
      );
  }

  public getPortabilityRequestsStatus(protocol: string): Observable<IPortabilityStatusHistoryItem[]> {
    return this.api.get<IPortabilityStatusHistoryResponse>(`/stvm/orquestrador/api/v1/custody/operations/status/${protocol}`)
      .pipe(
        map(response => response.statusHistory)
      );
  }

  private getSessionData(cblc: string): { cblc: string } & IPortabilitySessionData {
    return {
      cblc,
      portabilityTypeData: getParsedFromSession<PortabilityType>('portabilityTypeData'),
      portabilityInstitutionData: getParsedFromSession<IInstitutionData>('portabilityInstitutionData'),
      portabilityProductsData: getParsedFromSession<IProductsData>('portabilityProductsData'),
      portabilityEffectuationTypeData: getParsedFromSession<IEffectuationType>('portabilityEffectuationTypeData'),
    };
  }
}
